import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
} from 'react-native';
import { theme } from '../../theme/theme';
import { HeartIcon, CommentIcon, MoreIcon, EditIcon, TrashIcon } from '../icons';

interface User {
  id: string;
  name: string;
  username: string;
  avatarUrl?: string;
}

interface Reply {
  id: string;
  content: string;
  user: User;
  likesCount: number;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
  isLiked?: boolean;
}

interface Comment {
  id: string;
  content: string;
  user: User;
  likesCount: number;
  repliesCount: number;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
  replies?: Reply[];
  isLiked?: boolean;
}

interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  onLike: (commentId: string) => void;
  onReply: (comment: Comment) => void;
  onEdit: (comment: Comment) => void;
  onDelete: (commentId: string) => void;
  onUserPress: (userId: string) => void;
  showReplies?: boolean;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserId,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onUserPress,
  showReplies = true,
}) => {
  const [showAllReplies, setShowAllReplies] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return '刚刚';
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}分钟前`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}小时前`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)}天前`;
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  };

  const handleLike = () => {
    onLike(comment.id);
  };

  const handleReply = () => {
    onReply(comment);
  };

  const handleEdit = () => {
    onEdit(comment);
    setShowActions(false);
  };

  const handleDelete = () => {
    Alert.alert(
      '删除评论',
      '确定要删除这条评论吗？此操作不可撤销。',
      [
        { text: '取消', style: 'cancel' },
        {
          text: '删除',
          style: 'destructive',
          onPress: () => {
            onDelete(comment.id);
            setShowActions(false);
          },
        },
      ]
    );
  };

  const canEdit = currentUserId === comment.user.id;
  const canDelete = currentUserId === comment.user.id; // 或者是作品作者

  const displayedReplies = showAllReplies 
    ? comment.replies 
    : comment.replies?.slice(0, 2);

  const hasMoreReplies = comment.replies && comment.replies.length > 2;

  const renderReply = (reply: Reply) => (
    <View key={reply.id} style={styles.replyContainer}>
      <TouchableOpacity onPress={() => onUserPress(reply.user.id)}>
        <Image
          source={{ 
            uri: reply.user.avatarUrl || 'https://via.placeholder.com/32x32?text=A' 
          }}
          style={styles.replyAvatar}
        />
      </TouchableOpacity>
      
      <View style={styles.replyContent}>
        <View style={styles.replyHeader}>
          <TouchableOpacity onPress={() => onUserPress(reply.user.id)}>
            <Text style={styles.replyUsername}>{reply.user.name}</Text>
          </TouchableOpacity>
          <Text style={styles.replyTime}>
            {formatTimeAgo(reply.createdAt)}
            {reply.isEdited && ' (已编辑)'}
          </Text>
        </View>
        
        <Text style={styles.replyText}>{reply.content}</Text>
        
        <View style={styles.replyActions}>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={() => onLike(reply.id)}
          >
            <HeartIcon 
              size={14} 
              color={reply.isLiked ? '#ef4444' : theme.colors.textSecondary}
              filled={reply.isLiked}
            />
            {reply.likesCount > 0 && (
              <Text style={styles.actionCount}>{reply.likesCount}</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={() => onUserPress(comment.user.id)}>
        <Image
          source={{ 
            uri: comment.user.avatarUrl || 'https://via.placeholder.com/40x40?text=A' 
          }}
          style={styles.avatar}
        />
      </TouchableOpacity>
      
      <View style={styles.content}>
        <View style={styles.header}>
          <View style={styles.userInfo}>
            <TouchableOpacity onPress={() => onUserPress(comment.user.id)}>
              <Text style={styles.username}>{comment.user.name}</Text>
            </TouchableOpacity>
            <Text style={styles.time}>
              {formatTimeAgo(comment.createdAt)}
              {comment.isEdited && ' (已编辑)'}
            </Text>
          </View>
          
          {(canEdit || canDelete) && (
            <TouchableOpacity
              style={styles.moreButton}
              onPress={() => setShowActions(!showActions)}
            >
              <MoreIcon size={16} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
        
        <Text style={styles.commentText}>{comment.content}</Text>
        
        <View style={styles.actions}>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={handleLike}
          >
            <HeartIcon 
              size={16} 
              color={comment.isLiked ? '#ef4444' : theme.colors.textSecondary}
              filled={comment.isLiked}
            />
            {comment.likesCount > 0 && (
              <Text style={styles.actionCount}>{comment.likesCount}</Text>
            )}
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={handleReply}
          >
            <CommentIcon size={16} color={theme.colors.textSecondary} />
            <Text style={styles.actionText}>回复</Text>
          </TouchableOpacity>
        </View>

        {/* 操作菜单 */}
        {showActions && (
          <View style={styles.actionsMenu}>
            {canEdit && (
              <TouchableOpacity style={styles.menuItem} onPress={handleEdit}>
                <EditIcon size={16} color={theme.colors.text} />
                <Text style={styles.menuText}>编辑</Text>
              </TouchableOpacity>
            )}
            {canDelete && (
              <TouchableOpacity style={styles.menuItem} onPress={handleDelete}>
                <TrashIcon size={16} color="#ef4444" />
                <Text style={[styles.menuText, { color: '#ef4444' }]}>删除</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        
        {/* 回复列表 */}
        {showReplies && comment.replies && comment.replies.length > 0 && (
          <View style={styles.repliesContainer}>
            {displayedReplies?.map(renderReply)}
            
            {hasMoreReplies && !showAllReplies && (
              <TouchableOpacity
                style={styles.showMoreButton}
                onPress={() => setShowAllReplies(true)}
              >
                <Text style={styles.showMoreText}>
                  查看更多回复 ({comment.replies.length - 2})
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: theme.spacing.md,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.xs,
  },
  userInfo: {
    flex: 1,
  },
  username: {
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
  },
  time: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs / 2,
  },
  moreButton: {
    padding: theme.spacing.xs,
  },
  commentText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    lineHeight: 20,
    marginBottom: theme.spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: theme.spacing.lg,
    paddingVertical: theme.spacing.xs,
  },
  actionCount: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.textSecondary,
    marginLeft: theme.spacing.xs,
  },
  actionText: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.textSecondary,
    marginLeft: theme.spacing.xs,
  },
  actionsMenu: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  menuText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.text,
    marginLeft: theme.spacing.sm,
  },
  repliesContainer: {
    marginTop: theme.spacing.md,
  },
  replyContainer: {
    flexDirection: 'row',
    marginBottom: theme.spacing.sm,
  },
  replyAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: theme.spacing.sm,
  },
  replyContent: {
    flex: 1,
  },
  replyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  replyUsername: {
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.medium,
    color: theme.colors.text,
    marginRight: theme.spacing.sm,
  },
  replyTime: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.textSecondary,
  },
  replyText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.text,
    lineHeight: 18,
    marginBottom: theme.spacing.xs,
  },
  replyActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  showMoreButton: {
    paddingVertical: theme.spacing.sm,
  },
  showMoreText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.primary,
  },
});
